import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import PropTypes from "prop-types";
import {
  tooltipContentStyle,
  tooltipItemStyle,
  tooltipLabelStyle,
  chartColors,
} from "./ChartStyles";
import "./CategoryOverviewTile.scss";

const CategoryOverviewTile = ({
  title = "Category Overview",
  subtitle = "Last Month",
  data = [],
  valueKey = "value",
  labelKey = "name",
}) => {
  // Sort and prepare data
  const sortedData = [...data].sort((a, b) => b[valueKey] - a[valueKey]);
  const topFive = sortedData.slice(0, 5);
  const othersTotal = sortedData.slice(5).reduce((sum, d) => sum + d[valueKey], 0);

  const chartData =
    othersTotal > 0
      ? [...topFive, { [labelKey]: "Others", [valueKey]: othersTotal }]
      : topFive;

  const COLORS = [
    "#2982d9", // light blue
    "#E4833E", // orange
    "#A12E76", // deep blue
    "#90C15D", // medium blue
    "#560BAD", // purple
    "#939598", // grey (for Others)
  ];

  const total = chartData.reduce((sum, d) => sum + d[valueKey], 0);

  return (
    <div
      className="category-overview-tile d-flex justify-content-between align-items-center p-4"
      style={{ width: `100%`, height: `100%` }}
    >
      {/* Left Side: Labels */}
      <div className="tile-summary text-start flex-grow-1">
        <h6 className="tile-title mb-1">{title}</h6>
        <p className="tile-subtitle mb-3">{subtitle}</p>
        <ul className="list-unstyled mb-0 categorys-list">
          {chartData.map((entry, index) => (
            <li
              key={index}
              className="d-flex align-items-center justify-content-between mb-1"
            >
              <div className="d-flex align-items-center">
                <span
                  className="legend-dot me-2"
                  style={{
                    backgroundColor: COLORS[index % COLORS.length],
                  }}
                ></span>
                <span className="legend-label small text-muted">
                  {entry[labelKey]}
                </span>
              </div>
              <span className="legend-value small text-muted">
                {((entry[valueKey] / total) * 100).toFixed(0)}%
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Right Side: Donut Chart */}
      <div className="tile-chart">
        <ResponsiveContainer>
          <PieChart>
            <Tooltip
              formatter={(value, name) => [
                `${((value / total) * 100).toFixed(1)}%`,
                name,
              ]}
              contentStyle={tooltipContentStyle}
              itemStyle={tooltipItemStyle}
              labelStyle={tooltipLabelStyle}
            />
            <Pie
              data={chartData}
              dataKey={valueKey}
              nameKey={labelKey}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={60}
              stroke="none"
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

CategoryOverviewTile.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  data: PropTypes.array.isRequired,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default CategoryOverviewTile;
